import { useCallback } from 'react';
import type { UseBoundStore, StoreApi } from 'zustand';
import { ComponentConfigParameter, ComponentConfigParameterGroup } from 'common/stack/ComponentConfigDocuments';
import { ParameterGroupValues } from 'common/stack/ComponentWithValuesDocuments';
import { Help } from 'client/components/Help';
import { ConfigurationInput } from 'client/components/Input/ConfigurationInput';

const parameterTypeToInputType: Record<ComponentConfigParameter['type'], string> = {
  string: 'text',
  integer: 'number',
  secret: 'password',
  boolean: 'boolean',
};

type State = { parameterGroups: ComponentConfigParameterGroup[]; values: ParameterGroupValues[] };

interface ConfigurationItemInputProps {
  store?: UseBoundStore<StoreApi<State>>;
  value?: ParameterGroupValues;
  path?: string;
  readOnly?: boolean;
  parameterGroupIdentifier: string;
  parameters: ComponentConfigParameter;
  onChange: (groupLabel: string, parameterLabel: string, value: any) => void;
}

export const ConfigurationItemInput = ({
  store,
  value: valueProp,
  path,
  readOnly,
  parameterGroupIdentifier,
  parameters,
  onChange,
}: ConfigurationItemInputProps) => {
  let value;
  if (store) {
    // Fetch value directly from the store, and only update if own value changes
    value = store(
      useCallback(
        (state: State) =>
          state.values
            .find((item) => item.identifier === parameterGroupIdentifier)
            ?.parameters.find((param) => param.identifier === parameters.identifier)?.value ?? '',
        [parameterGroupIdentifier, parameters.identifier]
      )
    );
  } else {
    value = valueProp?.parameters.find((param) => param.identifier === parameters.identifier)?.value ?? '';
  }

  const handleChange = useCallback(
    (value: any) => {
      if (readOnly) return;
      onChange(parameterGroupIdentifier, parameters.identifier, value);
    },
    [readOnly, onChange]
  );

  return (
    <>
      <ConfigurationInput
        id={parameters.identifier}
        label={parameters.name}
        type={parameterTypeToInputType[parameters.type]}
        value={value}
        path={path + '.value'}
        canCopy={readOnly}
        onChange={handleChange}
      />
      {!!parameters.description && <Help>{parameters.description}</Help>}
    </>
  );
};
